import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Close, HelpOutline, Menu as MenuIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Menu as MuiMenu,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { MouseEvent, ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useProfile } from '../api';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { useAuth } from '../auth';
import Avatar from '../avatar/Avatar';
import Menu from '../menu/Menu';
import MenuDrawer from '../menu-drawer/MenuDrawer';
import { disableMockApi, isMockApiEnabled } from '../utils/mockApi';

export interface AppBarProps {
  children?: ReactNode;
  position?: MuiAppBarProps['position'];
  title?: ReactNode;
}

const reactRouterActive = theme => ({
  '&.active': {
    color: theme.palette.primary.contrastText,
  },
});

const AppBar = ({ position = 'fixed', title }: AppBarProps) => {
  const { loginRedirect, logoutRedirect } = useAuth();
  const { profile } = useProfile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);

  const portalVersion = import.meta.env.VITE_PORTAL_VERSION || 'local';

  const handleAvatarClick = (event: MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  return (
    <>
      <MuiAppBar
        component="nav"
        position={position}
        sx={{
          visibility: { xs: 'visible', sm: 'hidden' },
          width: { xs: '100%', sm: 0 },
          height: { xs: 'auto', sm: 0 },
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          variant="regular"
          sx={{ display: 'grid', gridTemplateColumns: '[left] 1fr [center] auto [right] 1fr' }}
        >
          <Box sx={{ gridColumn: 'left' }}>
            <Box component={Logo} sx={{ color: 'white', height: 48, width: 32, mr: 3, verticalAlign: 'middle' }} />
          </Box>
          {title && (
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', gridColumn: 'center' }}>
              {title}
            </Typography>
          )}

          <Box sx={{ gridColumn: 'right', ml: 'auto' }}>
            <IconButton color="inherit" edge="end" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <Close fontSize="large" /> : <MenuIcon fontSize="large" />}
            </IconButton>
          </Box>
        </Toolbar>
      </MuiAppBar>
      {position === 'fixed' && <Toolbar sx={{ display: { xs: 'block', sm: 'none' } }} />}

      <MuiAppBar
        component="nav"
        position={position}
        sx={{ visibility: { xs: 'hidden', sm: 'visible' }, zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <Toolbar variant="dense" sx={{ display: 'flex', alignItems: 'center' }}>
          <Box component={Logo} sx={{ color: 'white', flexShrink: 0, height: 48, width: 32, mr: 3 }} />

          <Menu onItemClick={() => setMenuOpen(false)} />

          <Box sx={{ flex: 1 }} />

          {isMockApiEnabled() && (
            <Button
              color="inherit"
              onClick={() => disableMockApi()}
              size="small"
              variant="outlined"
              sx={{ gap: 1, alignItems: 'center', mx: 2 }}
            >
              <Box
                sx={theme => ({ borderRadius: 9999, backgroundColor: theme.palette.success.main, height: 8, width: 8 })}
              ></Box>
              Mock API active
            </Button>
          )}
          <AuthenticatedTemplate>
            <IconButton
              component={NavLink}
              onClick={() => setMenuOpen(false)}
              sx={reactRouterActive}
              color="inherit"
              to="/help-center"
            >
              <HelpOutline
                sx={theme => ({
                  color: theme.palette.primary.contrastText,
                  m: 0,
                  cursor: 'pointer',
                })}
              />
            </IconButton>
            <IconButton onClick={handleAvatarClick} color="inherit" aria-label="account menu">
              <Avatar src={profile?.avatar} sx={{ height: 24, width: 24 }} />
            </IconButton>
            <MuiMenu anchorEl={accountMenuAnchor} open={Boolean(accountMenuAnchor)} onClose={handleAccountMenuClose}>
              <MenuItem
                component={NavLink}
                to="/profile"
                onClick={() => {
                  handleAccountMenuClose();
                  setMenuOpen(false);
                }}
              >
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleAccountMenuClose();
                  logoutRedirect();
                }}
              >
                Sign out
              </MenuItem>
              <Divider />
              <MenuItem disabled sx={{ opacity: '1 !important', fontSize: '0.75rem', color: 'text.secondary' }}>
                Version: {portalVersion}
              </MenuItem>
            </MuiMenu>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <Box display="flex" gap={1} sx={{ alignSelf: 'stretch', alignItems: 'center' }}>
              <Button onClick={loginRedirect} sx={{ color: 'rgba(255 255 255 / 0.7)' }}>
                Sign in
              </Button>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  alignSelf: 'stretch',
                  width: '1px',
                }}
              />
              <Button variant="contained" sx={{ color: 'rgba(255 255 255 / 0.7)' }} href="/register">
                Create account
              </Button>
            </Box>
          </UnauthenticatedTemplate>
        </Toolbar>
      </MuiAppBar>
      {position === 'fixed' && <Toolbar variant="dense" sx={{ display: { xs: 'none', sm: 'block' } }} />}

      <MenuDrawer open={menuOpen} onItemClick={() => setMenuOpen(false)} />
    </>
  );
};

export default AppBar;
